import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  ANALYTICS_CONSENT_CHANGE_EVENT,
  ANALYTICS_CONSENT_OPEN_EVENT,
  ANALYTICS_CONSENT_STATE,
  getAnalyticsConsentState,
  setAnalyticsConsentState,
} from "@/lib/consent";
import { resetAnalyticsTrackingState } from "@/lib/analytics";

function getBannerCopy(consentState) {
  if (consentState === ANALYTICS_CONSENT_STATE.GRANTED) {
    return "Analytics is currently enabled. You can switch back to essential-only browsing at any time.";
  }

  if (consentState === ANALYTICS_CONSENT_STATE.DENIED) {
    return "Analytics is currently off. You can keep essential-only browsing or enable analytics to measure visits and improve the site.";
  }

  return "We use optional analytics and advertising tags to understand visits and improve the site. Choose whether to allow these tools.";
}

export default function ConsentBanner() {
  const [consentState, setConsentState] = useState(() => getAnalyticsConsentState());
  const [isOpen, setIsOpen] = useState(() => getAnalyticsConsentState() === ANALYTICS_CONSENT_STATE.UNKNOWN);

  useEffect(() => {
    const handleConsentChange = (event) => {
      const nextState = event.detail?.state || getAnalyticsConsentState();

      setConsentState(nextState);
      setIsOpen(nextState === ANALYTICS_CONSENT_STATE.UNKNOWN ? true : false);
    };

    const handleOpenPreferences = () => {
      setConsentState(getAnalyticsConsentState());
      setIsOpen(true);
    };

    window.addEventListener(ANALYTICS_CONSENT_CHANGE_EVENT, handleConsentChange);
    window.addEventListener(ANALYTICS_CONSENT_OPEN_EVENT, handleOpenPreferences);

    return () => {
      window.removeEventListener(ANALYTICS_CONSENT_CHANGE_EVENT, handleConsentChange);
      window.removeEventListener(ANALYTICS_CONSENT_OPEN_EVENT, handleOpenPreferences);
    };
  }, []);

  const handleChoice = (nextState) => {
    const previousState = getAnalyticsConsentState();

    resetAnalyticsTrackingState();
    setAnalyticsConsentState(nextState);
    setConsentState(nextState);
    setIsOpen(false);

    if (previousState === ANALYTICS_CONSENT_STATE.GRANTED && nextState !== ANALYTICS_CONSENT_STATE.GRANTED) {
      window.location.reload();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6" aria-live="polite">
      <div
        className="mx-auto max-w-5xl rounded-3xl border border-border bg-card/95 shadow-medium backdrop-blur-xl"
        role="dialog"
        aria-label="Cookie preferences"
      >
        <div className="flex flex-col gap-5 p-5 sm:p-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-3xl">
            <p className="font-sans text-lg font-semibold text-foreground">Cookie preferences</p>
            <p className="mt-2 font-body text-sm leading-relaxed text-muted-foreground">
              {getBannerCopy(consentState)}
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              className="rounded-full px-5"
              onClick={() => handleChoice(ANALYTICS_CONSENT_STATE.DENIED)}
            >
              Essential only
            </Button>
            <Button
              type="button"
              className="rounded-full bg-primary px-5 text-white hover:bg-primary/90"
              onClick={() => handleChoice(ANALYTICS_CONSENT_STATE.GRANTED)}
            >
              Allow analytics
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
